/**
 * OCPP 2.0.1 adapter (Phase 7).
 *
 *   10a: BootNotification, Heartbeat, StatusNotification + plumbing.
 *   10b: Authorize, inbound RequestStartTransaction with topology resolution.
 *   10c: TransactionEvent (Started / Updated / Ended) covering domain
 *        startTransaction / stopTransaction / meterValues-during-tx,
 *        plus standalone MeterValues for non-tx samples and inbound
 *        RequestStopTransaction (with stringified-int txid bridging).
 *
 * Wire-format differences vs v16 live in `actions/*` — this class is
 * the same orchestrator pattern as `OcppV16Adapter` (build → validate
 * request → send → validate response → parse). The adapter additionally
 * owns per-transaction state (seqNo counter, minted txid, propagated
 * idToken) because v2.0.1 collapses Start/Stop/MeterValues-in-tx into
 * a single `TransactionEvent` action with sequence numbers — see the
 * `#txState` map below.
 */
import type { Topology } from '@/domain/Topology';
import { OcppValidationError } from '@/protocol/errors';
import {
  OCPP_MESSAGE_TYPE,
  type OcppCall,
  type OcppCallErrorFrame,
  type OcppCallResultFrame,
} from '@/protocol/OcppCall';
import type {
  AuthorizeResponse,
  BootNotificationDomain,
  BootNotificationResponse,
  ConfigurationKeyDescriptor,
  DataTransferDomain,
  DataTransferResponse,
  HeartbeatResponse,
  IncomingOcppEvent,
  MeterValuesDomain,
  OcppAdapter,
  StartTransactionDomain,
  StartTransactionResponse,
  StatusNotificationDomain,
  StopTransactionDomain,
  StopTransactionResponse,
} from '@/protocol/OcppAdapter';
import { validatorFor, type Direction, type Validator } from './schemas';
import {
  buildAuthorizeRequest,
  parseAuthorizeResponse,
  buildPncAuthorizeRequest,
  parsePncAuthorizeResponse,
  DEFAULT_ID_TOKEN_TYPE_V201,
  type IdTokenTypeV201,
  type PncAuthorizeDomain,
  type PncAuthorizeResult,
} from './actions/authorize';
import {
  buildBootNotificationRequest,
  parseBootNotificationResponse,
} from './actions/bootNotification';
import { DEFAULT_CONFIGURATION_KEYS_V201 } from './variables';
import { buildHeartbeatRequest, parseHeartbeatResponse } from './actions/heartbeat';
import { buildMeterValuesRequest } from './actions/meterValues';
import {
  buildDataTransferRequest,
  parseDataTransferResponse,
} from './actions/dataTransfer';
import {
  buildReportChargingProfilesRequest,
  buildNotifyChargingLimitRequest,
  buildClearedChargingLimitRequest,
  type ReportChargingProfilesDomain,
  type NotifyChargingLimitDomain,
  type ClearedChargingLimitDomain,
} from './actions/smartChargingReports';
import {
  buildNotifyEVChargingNeedsRequest,
  buildNotifyEVChargingScheduleRequest,
  type NotifyEVChargingNeedsDomain,
  type NotifyEVChargingScheduleDomain,
} from './actions/evCharging';
import {
  buildNotifyDisplayMessagesRequest,
  type NotifyDisplayMessagesDomain,
} from './actions/notifyDisplayMessages';
import {
  buildLogStatusNotificationRequest,
  buildNotifyEventRequest,
  type LogStatusNotificationDomain,
  type NotifyEventDomain,
} from './actions/diagnostics';
import {
  buildFirmwareStatusNotificationRequest,
  type FirmwareStatusNotificationDomain,
  buildPublishFirmwareStatusNotificationRequest,
  type PublishFirmwareStatusNotificationDomain,
} from './actions/firmware';
import {
  buildReservationStatusUpdateRequest,
  type ReservationStatusUpdateDomain,
} from './actions/reservation';
import {
  buildNotifyMonitoringReportRequest,
  type NotifyMonitoringReportDomain,
} from './actions/notifyMonitoringReport';
import {
  buildNotifyCustomerInformationRequest,
  type NotifyCustomerInformationDomain,
} from './actions/notifyCustomerInformation';
import {
  buildSignCertificateRequest,
  buildSecurityEventNotificationRequest,
  type SignCertificateDomain,
  type SecurityEventNotificationDomain,
} from './actions/certificates';
import {
  buildGet15118EVCertificateRequest,
  parseGet15118EVCertificateResponse,
  buildGetCertificateStatusRequest,
  parseGetCertificateStatusResponse,
  type Get15118EVCertificateDomain,
  type Get15118EVCertificateResponse,
  type GetCertificateStatusDomain,
  type GetCertificateStatusResponse,
} from './actions/pnc';
import {
  buildNotifyReportRequest,
  type NotifyReportDomain,
} from './actions/notifyReport';
import { buildStatusNotificationRequest } from './actions/statusNotification';
import {
  buildTransactionEventEnded,
  buildTransactionEventStarted,
  buildTransactionEventUpdated,
  parseTransactionEventIdTokenInfo,
} from './actions/transactionEvent';
import { dispatchIncoming, type TopologyGetter } from './actions/inbound';

export interface OcppCallSinkV201 {
  call(
    action: string,
    payload: Record<string, unknown>,
    timeoutMs?: number,
  ): { messageId: string; response: Promise<Record<string, unknown>> };
}

export interface OcppV201AdapterOptions {
  readonly sink: OcppCallSinkV201;
  /**
   * v2.0.1 inbound RequestStartTransaction carries an `evseId` (no
   * connectorId). The dispatcher resolves it to a global ConnectorId
   * via the station's current topology. Returns `null` when no
   * topology is available yet — callers see an InvalidIncoming event.
   */
  readonly topology?: TopologyGetter;
  /**
   * Per-station default IdTokenType for outbound Authorize. v2.0.1
   * requires every idToken to carry a type discriminator; the
   * version-neutral `OcppAdapter.authorize(idTag)` doesn't know
   * about it. Defaults to `'Central'` (CSMS-assigned identifier —
   * matches v1.6 idTag semantics). Surfacing this through the
   * Settings UI is deferred to a later session (touches
   * storage-schema).
   */
  readonly defaultIdTokenType?: IdTokenTypeV201;
  /**
   * Source of fresh numeric transactionIds. Defaults to a monotonic
   * counter starting at `Date.now() % 1_000_000`. Override in tests to
   * make wire payloads deterministic.
   */
  readonly mintTransactionId?: () => number;
  /**
   * Schema validator used for outbound/inbound frames. Defaults to the
   * OCPP 2.0.1 registry; OcppV21Adapter injects the 2.1 registry so the same
   * builders validate against the 2.1 schemas (2.1 is a backward-compatible
   * superset for the shared message set).
   */
  readonly schemaValidator?: (action: string, direction: Direction) => Validator;
  /** WebSocket subprotocol to advertise. Defaults to `ocpp2.0.1`. */
  readonly subprotocol?: string;
}

interface TxState {
  readonly transactionId: number;
  readonly idTag: string;
  readonly connectorId: number;
  seqNo: number;
}

export const V201_VERSION_LABEL = '2.0.1';

export class OcppV201Adapter implements OcppAdapter {
  readonly subprotocol: string;
  readonly #sink: OcppCallSinkV201;
  readonly #getTopology: TopologyGetter;
  readonly #defaultIdTokenType: IdTokenTypeV201;
  readonly #mintTxId: () => number;
  /** Schema validator (default = 2.0.1 registry; OcppV21Adapter injects 2.1). */
  readonly #validatorFor: (action: string, direction: Direction) => Validator;
  /** Active transactions keyed by domain TransactionId (number). */
  readonly #txState = new Map<number, TxState>();

  constructor(opts: OcppV201AdapterOptions | OcppCallSinkV201) {
    if ('call' in opts) {
      this.#sink = opts;
      this.#getTopology = () => null;
      this.#defaultIdTokenType = DEFAULT_ID_TOKEN_TYPE_V201;
      this.#mintTxId = defaultMintTxId();
      this.#validatorFor = validatorFor;
      this.subprotocol = 'ocpp2.0.1';
    } else {
      this.#sink = opts.sink;
      this.#getTopology = opts.topology ?? (() => null);
      this.#defaultIdTokenType = opts.defaultIdTokenType ?? DEFAULT_ID_TOKEN_TYPE_V201;
      this.#mintTxId = opts.mintTransactionId ?? defaultMintTxId();
      this.#validatorFor = opts.schemaValidator ?? validatorFor;
      this.subprotocol = opts.subprotocol ?? 'ocpp2.0.1';
    }
  }

  /** Adapter-level helper to swap topology without rebuilding the adapter. */
  withTopology(_topology: Topology): OcppV201Adapter {
    return new OcppV201Adapter({
      sink: this.#sink,
      topology: () => _topology,
      defaultIdTokenType: this.#defaultIdTokenType,
      mintTransactionId: this.#mintTxId,
      schemaValidator: this.#validatorFor,
      subprotocol: this.subprotocol,
    });
  }

  // -------- outbound (Session 10a) --------

  bootNotification(
    req: BootNotificationDomain,
  ): OcppCall<BootNotificationDomain, BootNotificationResponse> {
    return this.#send(
      'BootNotification',
      req,
      buildBootNotificationRequest,
      parseBootNotificationResponse,
    );
  }

  heartbeat(): OcppCall<Record<string, never>, HeartbeatResponse> {
    return this.#send('Heartbeat', {}, buildHeartbeatRequest, parseHeartbeatResponse);
  }

  statusNotification(
    req: StatusNotificationDomain,
  ): OcppCall<StatusNotificationDomain, Record<string, never>> {
    return this.#send('StatusNotification', req, buildStatusNotificationRequest, () => ({}));
  }

  authorize(idTag: string): OcppCall<{ idTag: string }, AuthorizeResponse> {
    return this.#send(
      'Authorize',
      { idTag },
      () => buildAuthorizeRequest(idTag, this.#defaultIdTokenType),
      parseAuthorizeResponse,
    );
  }

  /**
   * ISO 15118 Plug & Charge authorize: idToken typed `eMAID`, with the EV's
   * contract certificate (PEM) and/or OCSP hash data. The CSMS validates the
   * cert and returns `certificateStatus` alongside the authorization status.
   */
  authorizePnC(req: PncAuthorizeDomain): OcppCall<PncAuthorizeDomain, PncAuthorizeResult> {
    return this.#send('Authorize', req, buildPncAuthorizeRequest, parsePncAuthorizeResponse);
  }

  /**
   * v2.0.1 has no separate StartTransaction — the CP picks the txid
   * and emits `TransactionEvent(Started)`. The version-neutral
   * `StartTransactionResponse` is synthesised here: the txid comes
   * from `#mintTxId()`, the idTagInfo from the CSMS response.
   */
  startTransaction(
    req: StartTransactionDomain,
  ): OcppCall<StartTransactionDomain, StartTransactionResponse> {
    const transactionId = this.#mintTxId();
    const connectorIdAsNumber = req.connectorId as unknown as number;
    this.#txState.set(transactionId, {
      transactionId,
      idTag: req.idTag,
      connectorId: connectorIdAsNumber,
      seqNo: 0,
    });
    const wire = buildTransactionEventStarted({
      transactionId,
      req,
      idTokenType: this.#defaultIdTokenType,
    });
    const reqValid = this.#validatorFor('TransactionEvent', 'request')(wire);
    if (!reqValid.valid) {
      this.#txState.delete(transactionId);
      throw new OcppValidationError('TransactionEvent', 'request', reqValid.errors);
    }
    const sent = this.#sink.call('TransactionEvent', wire);
    const response = sent.response.then((payload) => {
      const resValid = this.#validatorFor('TransactionEvent', 'response')(payload);
      if (!resValid.valid) {
        throw new OcppValidationError('TransactionEvent', 'response', resValid.errors);
      }
      const idTagInfo = parseTransactionEventIdTokenInfo(payload) ?? {
        status: 'Accepted' as const,
      };
      const out: StartTransactionResponse = {
        transactionId,
        idTagInfo,
      };
      return out;
    });
    return { messageId: sent.messageId, action: 'TransactionEvent', request: req, response };
  }

  stopTransaction(
    req: StopTransactionDomain,
  ): OcppCall<StopTransactionDomain, StopTransactionResponse> {
    const txid = req.transactionId as unknown as number;
    const state = this.#txState.get(txid);
    // Permissive: if we have no state (e.g. recovered transaction
    // started in a previous tab), still send Ended with seqNo=1 and a
    // connectorId derived from the topology (best effort) or 0.
    const seqNo = state ? state.seqNo + 1 : 1;
    const connectorId = state?.connectorId ?? 0;
    const wire = buildTransactionEventEnded({
      transactionId: txid,
      seqNo,
      req,
      connectorId,
      ...(state ? { idTag: state.idTag } : {}),
      idTokenType: this.#defaultIdTokenType,
    });
    const reqValid = this.#validatorFor('TransactionEvent', 'request')(wire);
    if (!reqValid.valid) {
      throw new OcppValidationError('TransactionEvent', 'request', reqValid.errors);
    }
    const sent = this.#sink.call('TransactionEvent', wire);
    this.#txState.delete(txid);
    const response = sent.response.then((payload) => {
      const resValid = this.#validatorFor('TransactionEvent', 'response')(payload);
      if (!resValid.valid) {
        throw new OcppValidationError('TransactionEvent', 'response', resValid.errors);
      }
      const idTagInfo = parseTransactionEventIdTokenInfo(payload);
      const out: StopTransactionResponse = idTagInfo ? { idTagInfo } : {};
      return out;
    });
    return { messageId: sent.messageId, action: 'TransactionEvent', request: req, response };
  }

  /**
   * MeterValues dispatch:
   *   - If `req.transactionId` matches an active tx → emit
   *     `TransactionEvent(Updated)` with the samples embedded under
   *     `meterValue: [...]` (matches v2.0.1 §5.1.10 — per-tx metering
   *     belongs IN the TransactionEvent stream, not a separate action).
   *   - Otherwise → emit the standalone v2.0.1 `MeterValues` action
   *     (clock-aligned samples for an idle EVSE, etc.).
   */
  meterValues(req: MeterValuesDomain): OcppCall<MeterValuesDomain, Record<string, never>> {
    if (req.transactionId !== undefined) {
      const txid = req.transactionId as unknown as number;
      const state = this.#txState.get(txid);
      if (state) {
        state.seqNo += 1;
        const wire = buildTransactionEventUpdated({
          transactionId: txid,
          seqNo: state.seqNo,
          timestamp: req.meterValue[0]?.timestamp ?? new Date().toISOString(),
          connectorId: state.connectorId,
          meterValue: req.meterValue.map((mv) => ({
            timestamp: mv.timestamp,
            sampledValue: mv.sampledValue.map((sv) => ({
              value: Number(sv.value),
              ...(sv.measurand !== undefined ? { measurand: sv.measurand } : {}),
              ...(sv.context !== undefined ? { context: sv.context } : {}),
              ...(sv.unit !== undefined ? { unitOfMeasure: { unit: sv.unit } } : {}),
            })),
          })),
        });
        const reqValid = this.#validatorFor('TransactionEvent', 'request')(wire);
        if (!reqValid.valid) {
          throw new OcppValidationError('TransactionEvent', 'request', reqValid.errors);
        }
        const sent = this.#sink.call('TransactionEvent', wire);
        const response = sent.response.then((payload) => {
          const resValid = this.#validatorFor('TransactionEvent', 'response')(payload);
          if (!resValid.valid) {
            throw new OcppValidationError('TransactionEvent', 'response', resValid.errors);
          }
          return {} as Record<string, never>;
        });
        return { messageId: sent.messageId, action: 'TransactionEvent', request: req, response };
      }
    }
    return this.#send('MeterValues', req, buildMeterValuesRequest, () => ({}));
  }

  dataTransfer(
    req: DataTransferDomain,
  ): OcppCall<DataTransferDomain, DataTransferResponse> {
    // Session 19a — v2.0.1 DataTransfer wire shape matches v1.6 for the
    // fields we use; reuse the standard build → validate → send → parse
    // path. (Previously threw OcppNotImplementedError.)
    return this.#send(
      'DataTransfer',
      req,
      buildDataTransferRequest,
      parseDataTransferResponse,
    );
  }

  /**
   * Session 16 — v2.0.1-only outbound `NotifyReport` (OCPP 2.0.1 §B.07).
   * Fired by the store's `handleCsmsCall` after replying `Accepted` to
   * a `GetBaseReport`. Returns the live `OcppCall` so the store can
   * chain a follow-up chunk in the same async path if `tbc: true` is
   * ever used (we ship a single chunk for now).
   */
  notifyReport(
    req: NotifyReportDomain,
  ): OcppCall<NotifyReportDomain, Record<string, never>> {
    return this.#send('NotifyReport', req, buildNotifyReportRequest, () => ({}));
  }

  /**
   * Session 19c — v2.0.1-only Smart Charging outbound. ReportChargingProfiles
   * is the paged reply to an inbound GetChargingProfiles; NotifyChargingLimit
   * / ClearedChargingLimit announce external-limit changes. All three are
   * CALLs whose response payload is empty.
   */
  reportChargingProfiles(
    req: ReportChargingProfilesDomain,
  ): OcppCall<ReportChargingProfilesDomain, Record<string, never>> {
    return this.#send(
      'ReportChargingProfiles',
      req,
      buildReportChargingProfilesRequest,
      () => ({}),
    );
  }

  // ── CP-initiated capability surface (tech-debt #12) ──────────────────────
  // The methods below are messages a real charge point sends to the CSMS on
  // its own initiative (local load control, certificate renewal, security
  // events, ISO 15118 PnC, reservation expiry). The simulator has no UI button
  // for most of them and models no external charging-limit source, so several
  // have no internal trigger today — but they ARE part of the validated
  // outbound surface (each is exercised by a capability test that round-trips
  // the frame through the pinned Ajv schema). reservationStatusUpdate is wired
  // for real by the reservation auto-expiry path (csmsCallHandler, #3). Do not
  // treat "no caller in src/" as dead code: these are deliberate capabilities.

  notifyChargingLimit(
    req: NotifyChargingLimitDomain,
  ): OcppCall<NotifyChargingLimitDomain, Record<string, never>> {
    return this.#send(
      'NotifyChargingLimit',
      req,
      buildNotifyChargingLimitRequest,
      () => ({}),
    );
  }

  clearedChargingLimit(
    req: ClearedChargingLimitDomain,
  ): OcppCall<ClearedChargingLimitDomain, Record<string, never>> {
    return this.#send(
      'ClearedChargingLimit',
      req,
      buildClearedChargingLimitRequest,
      () => ({}),
    );
  }

  /** CP→CSMS V2G negotiation: the EV's requested energy-transfer needs. */
  notifyEVChargingNeeds(
    req: NotifyEVChargingNeedsDomain,
  ): OcppCall<NotifyEVChargingNeedsDomain, Record<string, never>> {
    return this.#send(
      'NotifyEVChargingNeeds',
      req,
      buildNotifyEVChargingNeedsRequest,
      () => ({}),
    );
  }

  /** CP→CSMS V2G negotiation: the negotiated charging schedule. */
  notifyEVChargingSchedule(
    req: NotifyEVChargingScheduleDomain,
  ): OcppCall<NotifyEVChargingScheduleDomain, Record<string, never>> {
    return this.#send(
      'NotifyEVChargingSchedule',
      req,
      buildNotifyEVChargingScheduleRequest,
      () => ({}),
    );
  }

  /** Session 19d — paged reply to an inbound GetDisplayMessages. */
  notifyDisplayMessages(
    req: NotifyDisplayMessagesDomain,
  ): OcppCall<NotifyDisplayMessagesDomain, Record<string, never>> {
    return this.#send(
      'NotifyDisplayMessages',
      req,
      buildNotifyDisplayMessagesRequest,
      () => ({}),
    );
  }

  /** Session 19e — diagnostics outbound: log-upload progress + device events. */
  logStatusNotification(
    req: LogStatusNotificationDomain,
  ): OcppCall<LogStatusNotificationDomain, Record<string, never>> {
    return this.#send(
      'LogStatusNotification',
      req,
      buildLogStatusNotificationRequest,
      () => ({}),
    );
  }

  notifyEvent(req: NotifyEventDomain): OcppCall<NotifyEventDomain, Record<string, never>> {
    return this.#send('NotifyEvent', req, buildNotifyEventRequest, () => ({}));
  }

  /** Session 20 — firmware-update lifecycle progress (carries the requestId). */
  firmwareStatusNotification(
    req: FirmwareStatusNotificationDomain,
  ): OcppCall<FirmwareStatusNotificationDomain, Record<string, never>> {
    return this.#send(
      'FirmwareStatusNotification',
      req,
      buildFirmwareStatusNotificationRequest,
      () => ({}),
    );
  }

  /**
   * Local Controller role — progress of caching a firmware image published via
   * PublishFirmware (carries the originating requestId + published locations).
   */
  publishFirmwareStatusNotification(
    req: PublishFirmwareStatusNotificationDomain,
  ): OcppCall<PublishFirmwareStatusNotificationDomain, Record<string, never>> {
    return this.#send(
      'PublishFirmwareStatusNotification',
      req,
      buildPublishFirmwareStatusNotificationRequest,
      () => ({}),
    );
  }

  /** Session 21 — reservation expired / removed (not CSMS-initiated). */
  reservationStatusUpdate(
    req: ReservationStatusUpdateDomain,
  ): OcppCall<ReservationStatusUpdateDomain, Record<string, never>> {
    return this.#send(
      'ReservationStatusUpdate',
      req,
      buildReservationStatusUpdateRequest,
      () => ({}),
    );
  }

  /** Session 22 — paged reply to GetMonitoringReport. */
  notifyMonitoringReport(
    req: NotifyMonitoringReportDomain,
  ): OcppCall<NotifyMonitoringReportDomain, Record<string, never>> {
    return this.#send(
      'NotifyMonitoringReport',
      req,
      buildNotifyMonitoringReportRequest,
      () => ({}),
    );
  }

  /** Session 24 — paged reply to CustomerInformation(report=true). */
  notifyCustomerInformation(
    req: NotifyCustomerInformationDomain,
  ): OcppCall<NotifyCustomerInformationDomain, Record<string, never>> {
    return this.#send(
      'NotifyCustomerInformation',
      req,
      buildNotifyCustomerInformationRequest,
      () => ({}),
    );
  }

  /** Session 25 — CP submits a CSR for signing (cert renewal). */
  signCertificate(
    req: SignCertificateDomain,
  ): OcppCall<SignCertificateDomain, { status: string }> {
    return this.#send('SignCertificate', req, buildSignCertificateRequest, (p) => ({
      status: p['status'] as string,
    }));
  }

  /** Session 25 — CP reports a security event. */
  securityEventNotification(
    req: SecurityEventNotificationDomain,
  ): OcppCall<SecurityEventNotificationDomain, Record<string, never>> {
    return this.#send(
      'SecurityEventNotification',
      req,
      buildSecurityEventNotificationRequest,
      () => ({}),
    );
  }

  /** Session 26 — ISO 15118 PnC: relay the EV certificate Install/Update request. */
  get15118EVCertificate(
    req: Get15118EVCertificateDomain,
  ): OcppCall<Get15118EVCertificateDomain, Get15118EVCertificateResponse> {
    return this.#send(
      'Get15118EVCertificate',
      req,
      buildGet15118EVCertificateRequest,
      parseGet15118EVCertificateResponse,
    );
  }

  /** Session 26 — ISO 15118 PnC: OCSP status of a certificate via the CSMS. */
  getCertificateStatus(
    req: GetCertificateStatusDomain,
  ): OcppCall<GetCertificateStatusDomain, GetCertificateStatusResponse> {
    return this.#send(
      'GetCertificateStatus',
      req,
      buildGetCertificateStatusRequest,
      parseGetCertificateStatusResponse,
    );
  }

  // -------- inbound --------

  parseIncoming(frame: unknown): IncomingOcppEvent {
    if (!Array.isArray(frame)) {
      return { kind: 'Unknown', messageId: '', action: '', rawPayload: {} };
    }
    const [type, messageId, action, payload] = frame as [
      number,
      string,
      string,
      Record<string, unknown>,
    ];
    if (type !== OCPP_MESSAGE_TYPE.CALL) {
      return {
        kind: 'Unknown',
        messageId: String(messageId),
        action: String(action ?? ''),
        rawPayload: {},
      };
    }
    // Session 11 / CR 2026-05-27 §1.3 — same pattern as v16: Ajv-validate
    // inbound payloads before dispatch. Unknown-action errors fall
    // through to the regular `dispatchIncoming` path (→ UnknownIncoming
    // → NotImplemented reply downstream).
    const wirePayload = payload ?? {};
    const v = this.#validatorFor(action, 'request')(wirePayload);
    if (
      !v.valid &&
      !v.errors.some((e) => e.startsWith('unknown action'))
    ) {
      return {
        kind: 'Invalid',
        messageId,
        action: 'Invalid',
        originalAction: action,
        reason: v.errors.join('; '),
        rawPayload: wirePayload,
      };
    }
    return dispatchIncoming(messageId, action, wirePayload, this.#getTopology);
  }

  // -------- capabilities --------

  supportedConfigurationKeys(): readonly ConfigurationKeyDescriptor[] {
    return DEFAULT_CONFIGURATION_KEYS_V201;
  }

  /**
   * v2.0.1 has no feature-profile concept (replaced by Component-level
   * variables). Returned as an empty list so the version-neutral
   * `supportedFeatureProfiles` API doesn't return anything misleading.
   */
  supportedFeatureProfiles(): readonly string[] {
    return [];
  }

  // -------- frame helpers --------

  #send<TReq, TRes>(
    action: string,
    domainReq: TReq,
    build: (req: TReq) => Record<string, unknown>,
    parse: (payload: Record<string, unknown>) => TRes,
  ): OcppCall<TReq, TRes> {
    const wire = build(domainReq);
    const reqValid = this.#validatorFor(action, 'request')(wire);
    if (!reqValid.valid) {
      throw new OcppValidationError(action, 'request', reqValid.errors);
    }
    const sent = this.#sink.call(action, wire);
    const response = sent.response.then((payload) => {
      const resValid = this.#validatorFor(action, 'response')(payload);
      if (!resValid.valid) {
        throw new OcppValidationError(action, 'response', resValid.errors);
      }
      return parse(payload);
    });
    return { messageId: sent.messageId, action, request: domainReq, response };
  }

  static buildCallResult(messageId: string, payload: Record<string, unknown>): OcppCallResultFrame {
    return [OCPP_MESSAGE_TYPE.CALLRESULT, messageId, payload];
  }

  static buildCallError(
    messageId: string,
    errorCode: string,
    errorDescription: string,
    details: Record<string, unknown> = {},
  ): OcppCallErrorFrame {
    return [OCPP_MESSAGE_TYPE.CALLERROR, messageId, errorCode, errorDescription, details];
  }
}

/**
 * Default txid minter: monotonic counter seeded from a wall-clock
 * offset so two adapters in the same process don't collide. Wraps
 * within OCPP's int32 range (`TransactionId.of()` rejects >2^31-1).
 */
function defaultMintTxId(): () => number {
  let next = (Date.now() % 1_000_000) + 1;
  return () => {
    const id = next;
    next = next + 1 > 2_147_483_647 ? 1 : next + 1;
    return id;
  };
}
